"use client";

import * as React from "react";
import { AlertCircle, ChevronDown, Check, Search } from "lucide-react";
import PhoneInputWithCountry from "react-phone-number-input/react-hook-form";
import {
  getCountryCallingCode,
  type Country,
  type Props as PhoneInputProps,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";

import { cn } from "../../utils/cn";
import { Label } from "./label";
import "./phone-input.css";

export interface CustomPhoneInputProps extends Omit<PhoneInputProps<any>, "onChange" | "value"> {
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  name: string;
  control: any;
  rules?: any;
}

interface CountrySelectProps {
  value?: Country;
  onChange: (value?: Country) => void;
  options: { value?: Country; label: string }[];
  disabled?: boolean;
  iconComponent: React.ComponentType<{ country: Country; label: string }>;
}

const CountrySelect = React.forwardRef<HTMLButtonElement, CountrySelectProps>(
  ({ value, onChange, options, disabled, iconComponent: Flag }, ref) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const searchInputRef = React.useRef<HTMLInputElement>(null);

    // Close when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Reset search on close
    React.useEffect(() => {
      if (!isOpen) {
        setSearch("");
      }
    }, [isOpen]);

    // Focus search when dropdown opens (avoids autoFocus lint rule)
    React.useEffect(() => {
      if (isOpen) {
        searchInputRef.current?.focus();
      }
    }, [isOpen]);

    const selectedOption = options.find((opt) => opt.value === value);

    const filteredOptions = options.filter((opt) => {
      if (!opt.value) return false;

      const countryName = opt.label.toLowerCase();
      const countryCode = opt.value.toLowerCase();
      let callingCode = "";
      try {
        callingCode = getCountryCallingCode(opt.value).toLowerCase();
      } catch (e) {
        // ignore
      }

      const searchLower = search.toLowerCase();
      return (
        countryName.includes(searchLower) ||
        countryCode.includes(searchLower) ||
        callingCode.includes(searchLower)
      );
    });

    return (
      <div className="relative flex items-center" ref={dropdownRef} dir="ltr">
        <button
          ref={ref}
          type="button"
          disabled={disabled}
          onClick={() => setIsOpen((prev) => !prev)}
          className="flex items-center gap-1.5 py-1 px-1.5 rounded-lg text-surface-on hover:bg-surface-container-low transition-colors disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:hover:bg-[#1A1C1E] focus:outline-none focus:bg-surface-container-low dark:focus:bg-[#1A1C1E]"
        >
          {value ? (
            <div className="w-5 h-4 shrink-0 overflow-hidden rounded-[2px] bg-foreground/10 flex items-center justify-center">
              <Flag country={value} label={selectedOption?.label || value} />
            </div>
          ) : (
            <span className="w-5 h-4 bg-outline-variant/30 rounded flex items-center justify-center text-[10px]">
              🌐
            </span>
          )}
          <ChevronDown
            className={cn(
              "h-3.5 w-3.5 opacity-50 transition-transform duration-200",
              isOpen && "rotate-180",
            )}
          />
        </button>

        {isOpen && (
          <div className="absolute top-full inset-s-0 mt-2 z-50 w-72 max-h-80 flex flex-col rounded-xl border border-outline-variant/40 bg-surface-container-lowest text-surface-on shadow-modal dark:bg-[#0D0E10] dark:border-[#434939] overflow-hidden animate-in fade-in-0 zoom-in-95 duration-100 origin-top-left">
            {/* Search Input */}
            <div className="p-2.5 border-b border-outline-variant/30 dark:border-[#434939]/30 flex items-center gap-2">
              <Search className="h-4 w-4 shrink-0 opacity-50 text-surface-on dark:text-white" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search country..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent border-none p-0 text-sm focus:ring-0 focus:outline-none text-surface-on dark:text-white placeholder:text-outline/70 dark:placeholder:text-outline"
              />
            </div>

            {/* Options List */}
            <div className="overflow-y-auto max-h-56 p-1 flex flex-col gap-0.5 custom-scrollbar">
              {filteredOptions.length === 0 ? (
                <div className="p-3 text-center text-xs text-outline/70 dark:text-outline">
                  No country found
                </div>
              ) : (
                filteredOptions.map((opt) => {
                  const isSelected = opt.value === value;
                  let dialCode = "";
                  try {
                    if (opt.value) {
                      dialCode = `+${getCountryCallingCode(opt.value)}`;
                    }
                  } catch {
                    // Country has no calling code in library data
                  }

                  return (
                    <button
                      key={opt.value || "default"}
                      type="button"
                      onClick={() => {
                        onChange(opt.value);
                        setIsOpen(false);
                      }}
                      className={cn(
                        "flex items-center justify-between w-full px-2.5 py-2 text-start text-sm rounded-lg hover:bg-primary/10 hover:text-primary transition-colors focus:outline-none focus:bg-primary/10 focus:text-primary text-surface-on dark:text-white",
                        isSelected && "bg-primary/10 text-primary font-medium dark:bg-primary/20",
                      )}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        {opt.value && (
                          <div className="w-5 h-4 shrink-0 overflow-hidden rounded-[2px] bg-foreground/10 flex items-center justify-center">
                            <Flag country={opt.value} label={opt.label} />
                          </div>
                        )}
                        <span className="truncate">{opt.label}</span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 text-xs text-outline/85 dark:text-[#C3C9B4]/85 ms-2">
                        <span>{dialCode}</span>
                        {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);
CountrySelect.displayName = "CountrySelect";

const PhoneInput = React.forwardRef<HTMLInputElement, CustomPhoneInputProps>(
  (
    {
      className,
      label,
      error,
      hint,
      required,
      id,
      defaultCountry = "SA",
      name,
      control,
      rules,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const inputId = id || generatedId;

    const inputElement = (
      <div className="relative w-full phone-input-container">
        <PhoneInputWithCountry
          name={name}
          control={control}
          rules={rules}
          id={inputId}
          defaultCountry={defaultCountry}
          countrySelectComponent={CountrySelect as any}
          international
          withCountryCallingCode

          className={cn(
            "flex h-11 w-full rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-4 py-2 text-sm font-body text-surface-on shadow-ambient-sm ring-offset-background placeholder:text-outline/70 focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-150 dark:bg-[#0D0E10] dark:border-[#434939] dark:text-white dark:placeholder:text-outline",
            error && "border-error focus-within:ring-error",
            className,
          )}
          ref={ref as any}
          required={required}
          style={{ ...props.style, direction: "ltr" } as React.CSSProperties}
          {...props}
        />
      </div>
    );

    if (!label && !error && !hint) {
      return inputElement;
    }

    return (
      <div className="flex flex-col gap-1.5 w-full">
        {label && (
          <Label htmlFor={inputId} required={required}>
            {label}
          </Label>
        )}
        {inputElement}
        {error && (
          <p className="flex items-center gap-1.5 text-xs text-error font-label font-bold animate-fade-in">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            {error}
          </p>
        )}
        {hint && !error && (
          <p className="text-xs text-surface-on-variant dark:text-[#C3C9B4] font-body mt-1">
            {hint}
          </p>
        )}
      </div>
    );
  },
);
PhoneInput.displayName = "PhoneInput";

export { PhoneInput };
